<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor Lockers</h5>
        <p class="page-sub">Manage numbered lockers assigned to visitors on check-in</p>
      </div>
      <button class="btn-primary-action" @click="openCreate">
        <font-awesome-icon icon="plus" /> Add Locker
      </button>
    </div>

    <!-- Stats -->
    <div v-if="!loading && lockers.length" class="locker-stats">
      <div class="stat-pill stat-pill--green">
        <font-awesome-icon icon="lock-open" />
        {{ availableCount }} Available
      </div>
      <div class="stat-pill stat-pill--amber">
        <font-awesome-icon icon="lock" />
        {{ occupiedCount }} Occupied
      </div>
      <div class="stat-pill stat-pill--grey">
        Total {{ lockers.length }}
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="locker-grid">
      <div v-for="n in 8" :key="n" class="locker-card locker-card--skeleton"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!lockers.length" class="empty-state">
      <font-awesome-icon icon="box-open" class="empty-icon" />
      <p class="empty-title">No lockers yet</p>
      <p class="empty-sub">Add lockers and assign them to visitor types that require secure storage.</p>
      <button class="btn-primary-action" @click="openCreate">Add First Locker</button>
    </div>

    <!-- Grid -->
    <div v-else class="locker-grid">
      <div
        v-for="l in lockers"
        :key="l.id"
        class="locker-card"
        :class="{
          'locker-card--occupied': l.occupied,
          'locker-card--inactive': !l.active,
        }"
      >
        <div class="locker-number">{{ l.number }}</div>
        <div v-if="l.label" class="locker-label">{{ l.label }}</div>
        <div class="locker-status">
          <span v-if="!l.active" class="status-chip status-chip--grey">Inactive</span>
          <span v-else-if="l.occupied" class="status-chip status-chip--amber">
            <font-awesome-icon icon="lock" /> Occupied
          </span>
          <span v-else class="status-chip status-chip--green">
            <font-awesome-icon icon="lock-open" /> Available
          </span>
        </div>
        <div class="locker-actions">
          <button class="btn-icon" title="Edit" @click="openEdit(l)">
            <font-awesome-icon icon="pencil-alt" />
          </button>
          <button class="btn-icon btn-icon--red" title="Delete" @click="confirmDelete(l)" :disabled="l.occupied">
            <font-awesome-icon icon="trash" />
          </button>
        </div>
      </div>
    </div>

    <!-- Create / Edit Modal -->
    <div v-if="modal" class="modal-backdrop" @mousedown.self="closeModal">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">{{ editing ? 'Edit Locker' : 'Add Locker' }}</h6>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Locker Number <span class="req">*</span></label>
            <input v-model="form.number" class="form-input" placeholder="e.g. A1, 101, B-03" maxlength="20" />
            <p class="form-hint">Must be unique across your lockers.</p>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Label / Description</label>
            <input v-model="form.label" class="form-input" placeholder="e.g. Ground Floor, Near Reception" maxlength="100" />
          </div>
          <div class="toggle-row" style="margin-top:16px;">
            <div class="toggle-info"><span class="toggle-name">Active</span></div>
            <label class="toggle-switch">
              <input type="checkbox" v-model="form.active" />
              <span class="toggle-track"></span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeModal">Cancel</button>
          <button class="btn-primary-action" @click="save" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving…' : (editing ? 'Save Changes' : 'Add Locker') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk Add Modal -->
    <div v-if="bulkModal" class="modal-backdrop" @mousedown.self="bulkModal = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">Bulk Add Lockers</h6>
          <button class="modal-close" @click="bulkModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;margin-bottom:14px;">Generate a numbered range of lockers at once.</p>
          <div class="two-col">
            <div class="form-row">
              <label class="form-label">Prefix (optional)</label>
              <input v-model="bulk.prefix" class="form-input" placeholder="e.g. A, B" maxlength="5" />
            </div>
            <div class="form-row">
              <label class="form-label">Count <span class="req">*</span></label>
              <input v-model.number="bulk.count" type="number" min="1" max="100" class="form-input" placeholder="e.g. 10" />
            </div>
          </div>
          <div class="two-col" style="margin-top:12px;">
            <div class="form-row">
              <label class="form-label">Start From</label>
              <input v-model.number="bulk.start" type="number" min="1" class="form-input" placeholder="1" />
            </div>
          </div>
          <p style="font-size:12px;color:#aab8cc;margin-top:10px;">
            Preview: {{ bulkPreview }}
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="bulkModal = false">Cancel</button>
          <button class="btn-primary-action" @click="doBulkAdd" :disabled="bulkSaving">
            <font-awesome-icon v-if="bulkSaving" icon="spinner" spin />
            {{ bulkSaving ? 'Adding…' : `Add ${bulk.count || 0} Lockers` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div v-if="deleteTarget" class="modal-backdrop" @mousedown.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">Delete Locker?</h6>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;">
            Delete locker <strong>{{ deleteTarget.number }}</strong>? This cannot be undone.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="deleting">
            <font-awesome-icon v-if="deleting" icon="spinner" spin />
            {{ deleting ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorLockers',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      lockers: [],
      modal: false,
      editing: null,
      saving: false,
      deleteTarget: null,
      deleting: false,
      bulkModal: false,
      bulkSaving: false,
      bulk: { prefix: '', count: 10, start: 1 },
      form: { number: '', label: '', active: true },
    }
  },
  computed: {
    availableCount() { return this.lockers.filter(l => l.active && !l.occupied).length },
    occupiedCount()  { return this.lockers.filter(l => l.occupied).length },
    bulkPreview() {
      const p = this.bulk.prefix || ''
      const s = this.bulk.start || 1
      const c = Math.min(this.bulk.count || 0, 5)
      if (!c) return '—'
      const samples = Array.from({ length: c }, (_, i) => `${p}${s + i}`)
      return samples.join(', ') + (this.bulk.count > 5 ? `… (${this.bulk.count} total)` : '')
    },
  },
  created() { this.load() },
  methods: {
    async load() {
      this.loading = true
      try {
        const r = await axios.get('visitors/lockers')
        this.lockers = r.data.data ?? []
      } catch { this.toast.error('Failed to load lockers.') }
      finally { this.loading = false }
    },
    openCreate() {
      this.form = { number: '', label: '', active: true }
      this.editing = null
      this.modal = true
    },
    openEdit(l) {
      this.editing = l
      this.form = { number: l.number, label: l.label ?? '', active: l.active }
      this.modal = true
    },
    closeModal() { this.modal = false; this.editing = null },
    async save() {
      if (!this.form.number.trim()) { this.toast.error('Locker number is required.'); return }
      this.saving = true
      try {
        if (this.editing) {
          await axios.put(`visitors/lockers/${this.editing.id}`, this.form)
          this.toast.success('Locker updated.')
        } else {
          await axios.post('visitors/lockers', this.form)
          this.toast.success('Locker added.')
        }
        this.closeModal()
        await this.load()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Save failed.')
      } finally { this.saving = false }
    },
    confirmDelete(l) { this.deleteTarget = l },
    async doDelete() {
      this.deleting = true
      try {
        await axios.delete(`visitors/lockers/${this.deleteTarget.id}`)
        this.toast.success('Locker deleted.')
        this.deleteTarget = null
        await this.load()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Delete failed.')
      } finally { this.deleting = false }
    },
    async doBulkAdd() {
      if (!this.bulk.count || this.bulk.count < 1) { this.toast.error('Count must be at least 1.'); return }
      this.bulkSaving = true
      let added = 0, failed = []
      const start = this.bulk.start || 1
      for (let i = 0; i < this.bulk.count; i++) {
        const number = `${this.bulk.prefix || ''}${start + i}`
        try {
          await axios.post('visitors/lockers', { number, active: true })
          added++
        } catch (e) {
          failed.push(number)
        }
      }
      this.bulkSaving = false
      this.bulkModal = false
      if (added) this.toast.success(`${added} locker${added !== 1 ? 's' : ''} added.`)
      if (failed.length) this.toast.warning(`${failed.length} skipped (already exist): ${failed.join(', ')}`)
      await this.load()
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#e4eaf2; }
.btn-danger { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }
.btn-icon { display:inline-flex; align-items:center; justify-content:center; width:30px; height:30px; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; cursor:pointer; color:#7a90b0; font-size:12px; }
.btn-icon:hover { background:#e4eaf2; }
.btn-icon--red:hover { background:#fee2e2; color:#dc2626; border-color:#fca5a5; }
.btn-icon:disabled { opacity:.4; cursor:not-allowed; }

/* Stats */
.locker-stats { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:20px; }
.stat-pill { display:inline-flex; align-items:center; gap:6px; padding:5px 14px; border-radius:20px; font-size:12px; font-weight:600; }
.stat-pill--green { background:#dcfce7; color:#16a34a; }
.stat-pill--amber { background:#fef9c3; color:#ca8a04; }
.stat-pill--grey  { background:#f1f5f9; color:#64748b; }

/* Grid */
.locker-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:12px; }

.locker-card {
  border:2px solid #e4eaf2; border-radius:12px; padding:16px 12px 12px;
  background:#fff; display:flex; flex-direction:column; align-items:center; gap:6px;
  position:relative; transition:border-color .15s;
}
.locker-card:hover { border-color:#c7d2e8; }
.locker-card--occupied  { border-color:#fcd34d; background:#fffbeb; }
.locker-card--inactive  { opacity:.55; }
.locker-card--skeleton  { min-height:110px; background:linear-gradient(90deg,#f1f5f9 25%,#e8edf5 50%,#f1f5f9 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border:none; border-radius:12px; }

@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.locker-number { font-size:22px; font-weight:800; color:#1a2b45; letter-spacing:.02em; }
.locker-label  { font-size:11px; color:#7a90b0; text-align:center; }
.locker-status { margin-top:2px; }
.locker-actions { display:flex; gap:4px; margin-top:6px; }

.status-chip { display:inline-flex; align-items:center; gap:4px; padding:2px 9px; border-radius:20px; font-size:11px; font-weight:600; }
.status-chip--green { background:#dcfce7; color:#16a34a; }
.status-chip--amber { background:#fef9c3; color:#ca8a04; }
.status-chip--grey  { background:#f1f5f9; color:#64748b; }

/* Empty state */
.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:3rem; color:#dde5f0; margin-bottom:16px; display:block; }
.empty-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0 0 20px; }

/* Modal */
.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box      { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm  { max-width:420px; }
.modal-header   { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close    { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body     { padding:20px 22px; display:flex; flex-direction:column; gap:4px; }
.modal-footer   { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.form-row   { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; width:100%; }
.form-input:focus { border-color:#004AAD; }
.form-hint  { font-size:11px; color:#aab8cc; margin:2px 0 0; }
.req { color:#dc2626; }

.two-col { display:grid; grid-template-columns:1fr 1fr; gap:12px; }

/* Toggle */
.toggle-row   { display:flex; align-items:center; justify-content:space-between; }
.toggle-info  { display:flex; flex-direction:column; }
.toggle-name  { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-switch { display:inline-flex; cursor:pointer; }
.toggle-switch input { display:none; }
.toggle-track { width:40px; height:22px; background:#dde5f0; border-radius:11px; position:relative; transition:background .2s; display:inline-block; }
.toggle-switch input:checked ~ .toggle-track { background:#004AAD; }
.toggle-track::after { content:''; position:absolute; top:3px; left:3px; width:16px; height:16px; background:#fff; border-radius:50%; transition:transform .2s; }
.toggle-switch input:checked ~ .toggle-track::after { transform:translateX(18px); }

@media (max-width:500px) { .locker-grid { grid-template-columns:repeat(auto-fill,minmax(110px,1fr)); } }
</style>
